import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import styles from './Toast.module.scss';
import {ToastPropsInterface} from './Toast.props.interface';
import CloseIcon from '../../assets/icons/24/cross.svg';

import DangerIcon from '../../assets/icons/24/danger.svg';
import WarningIcon from '../../assets/icons/24/warning.svg';
import SuccessIcon from '../../assets/icons/24/check.svg';

const ToastContext = createContext(undefined);

const Toast: React.FC<ToastPropsInterface> = (
    {
        message,
        type = 'success',
        size = 'small',
        duration = 3000,
        onClose,
    }) => {

    useEffect(() => {
        const timer = setTimeout(onClose, duration);
        return () => clearTimeout(timer);
    }, [duration, onClose]);

    return (
        <div className={[styles.item, styles[type], styles[size]].filter(Boolean).join(' ')}>
            <div className={styles.content}>
                <div className={styles.icon}>
                    {type === 'danger' ? <DangerIcon/> : null}
                    {type === 'warning' ? <WarningIcon/> : null}
                    {type === 'success' ? <SuccessIcon/> : null}
                </div>
                <div className={styles.message}>
                    {message}
                </div>
            </div>
            <button className={styles.close} onClick={onClose}>
                <CloseIcon/>
            </button>
        </div>
    );
};

export const ToastProvider = ({children}: any) => {
    const [toasts, setToasts] = useState<ToastPropsInterface[]>([]);

    const handleCreate = useCallback((data: any) => {
        setToasts((prevToasts) => [...prevToasts, {...data, id: Date.now()}]);
    }, []);

    const handleRemove = useCallback((data: any) => {
        data.onClose ? data.onClose() : null
        setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== data.id));
    }, []);

    return (
        <ToastContext.Provider value={handleCreate as any}>
            {children}
            <div className={styles.container}>
                <AnimatePresence>
                    {toasts.map((toast) => (
                        <motion.div
                            className={styles.motion}
                            key={toast.id}
                            initial={{opacity: 0, y: -20}}
                            animate={{opacity: 1, y: 0}}
                            exit={{opacity: 0, y: -20}}
                            transition={{duration: 0.15}}
                            layout>
                            <Toast {...toast} onClose={() => handleRemove(toast)}/>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);

    if (!context) {
        throw new Error('useToast должен использоваться внутри ToastProvider');
    }

    return context;
};

export default Toast;
